import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import api from '../lib/api'
import type {
  ActiveAnnouncementResponse,
  Announcement,
  AnnouncementCreate,
  AnnouncementListResponse,
} from '../types/announcement'

/** The single active announcement (or null). Polls so a freshly-posted one appears. */
export function useActiveAnnouncement() {
  return useQuery<ActiveAnnouncementResponse>({
    queryKey: ['announcement-active'],
    queryFn: async () => {
      const res = await api.get('/announcements/active')
      return res.data
    },
    refetchInterval: 60_000,
    staleTime: 30_000,
  })
}

/** Admin: full list of announcements. */
export function useAnnouncements() {
  return useQuery<AnnouncementListResponse>({
    queryKey: ['announcements'],
    queryFn: async () => {
      const res = await api.get('/announcements')
      return res.data
    },
  })
}

/** Admin: create a new announcement (retires any currently-active one). */
export function useCreateAnnouncement() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: AnnouncementCreate) => {
      const res = await api.post('/announcements', input)
      return res.data as Announcement
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['announcements'] })
      qc.invalidateQueries({ queryKey: ['announcement-active'] })
    },
  })
}

/** Admin: retire an announcement. */
export function useDeactivateAnnouncement() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: number) => {
      const res = await api.patch(`/announcements/${id}/deactivate`)
      return res.data as Announcement
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['announcements'] })
      qc.invalidateQueries({ queryKey: ['announcement-active'] })
    },
  })
}
